//c_asaaaiさんテンプレート

import styled from '@emotion/styled'
import React, { useEffect } from 'react'
import { Platform } from '@/firebase/firestore/Platform'
import { Sex } from '@/firebase/firestore/Sex'
import { VoiceChat } from '@/firebase/firestore/VoiceChats'
import Image from 'next/image'
import fitty from 'fitty'
import { useLocale } from '@/hooks/useLocale'

type Props = {
  targetId: string
  templateColor: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7
  uid: string
  name: string
  sex: Sex
  platform: Array<Platform>
  voiceChat: Array<VoiceChat>
  playStyle: string
  fontFamily: string
  fontColor: string
  playTimeWeekDayBegin: string
  playTimeWeekDayEnd: string
  playTimeWeekEndBegin: string
  playTimeWeekEndEnd: string
  playTime: string
  apexFavoriteWeapon: string
  apexMaxDamage: number
  apexKD: number
  hitokoto: string
  legends: number[]
}

export const Apex16: React.FC<Props> = ({
  name,
  sex,
  fontFamily,
  fontColor,
  playStyle,
  templateColor,
  voiceChat,
  playTimeWeekDayBegin,
  playTimeWeekDayEnd,
  playTimeWeekEndBegin,
  playTimeWeekEndEnd,
  playTime,
  apexFavoriteWeapon,
  apexMaxDamage,
  apexKD,
  hitokoto,
  legends,
  targetId,
}) => {
  const { t } = useLocale()
  useEffect(() => {
    fitty('#name', {
      maxSize: 24,
      minSize: 12,
    })
    fitty('#apexId', {
      maxSize: 16,
      minSize: 12,
    })
    fitty('#playTime', {
      maxSize: 16,
      minSize: 11,
    })
    fitty('#favoriteWeapon', {
      maxSize: 16,
      minSize: 8,
    })
    fitty('#playStyle', {
      maxSize: 16,
      minSize: 8,
    })
    fitty('#hitokoto', {
      maxSize: 28,
      minSize: 8,
    })
  }, [])
  return (
    <Container id={targetId} fontFamily={fontFamily} fontColor={fontColor}>
      <Image
        width="2048"
        height="1460"
        priority={true}
        unoptimized={true}
        src={`/static/images/profiles/apex16/${templateColor}.jpeg`}
        alt={`Apex自己紹介カード ${templateColor}`}
      />
      <Name>
        <span id="name">{name}</span>
      </Name>
      {sex === 0 && <Sex0 />}
      {sex === 1 && <Sex1 />}
      <VCText id="voiceChat" color={fontColor}>
        {voiceChat.includes(0) && ' Skype '}
        {voiceChat.includes(1) && ' LINE '}
        {voiceChat.includes(2) && ' プレステ '}
        {voiceChat.includes(3) && ' Discord '}
        {voiceChat.includes(5) && ' Switch '}
      </VCText>
      {legends.includes(0) && <Legend0 />}
      {legends.includes(1) && <Legend1 />}
      {legends.includes(2) && <Legend2 />}
      {legends.includes(3) && <Legend3 />}
      {legends.includes(4) && <Legend4 />}
      {legends.includes(5) && <Legend5 />}
      {legends.includes(6) && <Legend6 />}
      {legends.includes(7) && <Legend7 />}
      {legends.includes(8) && <Legend8 />}
      {legends.includes(9) && <Legend9 />}
      {legends.includes(10) && <Legend10 />}
      {legends.includes(11) && <Legend11 />}
      {legends.includes(12) && <Legend12 />}
      {legends.includes(13) && <Legend13 />}
      {legends.includes(14) && <Legend14 />}
      {legends.includes(15) && <Legend15 />}
      {legends.includes(16) && <Legend16 />}
      {legends.includes(17) && <Legend17 />}
      {legends.includes(18) && <Legend18 />}
      {legends.includes(19) && <Legend19 />}
      {legends.includes(20) && <Legend20 />}
      {playTime !== '' && (
        <PlayTime>
          <span id="playTime">{playTime}</span>
        </PlayTime>
      )}
      {playTime === '' && (
        <TimeWeekDay>
          {t.平日}
          {playTimeWeekDayBegin}
          {t.時開始} 〜 {playTimeWeekDayEnd}
          {t.時終了}
        </TimeWeekDay>
      )}
      {playTime === '' && (
        <TimeWeekEnd>
          {t.土日}
          {playTimeWeekEndBegin}
          {t.時開始} 〜 {playTimeWeekEndEnd}
          {t.時終了}
        </TimeWeekEnd>
      )}
      <FavoriteWeapon>
        <span id="favoriteWeapon">{apexFavoriteWeapon}</span>
      </FavoriteWeapon>
      <Hitokoto>
        <span id={'hitokoto'}>{hitokoto}</span>
      </Hitokoto>
      <PlayStyle>
        <span id="playStyle">{playStyle}</span>
      </PlayStyle>
      <MaxDamage>{apexMaxDamage}</MaxDamage>
      <KD>{apexKD}</KD>
    </Container>
  )
}

const Container = styled.div<{ fontFamily: string; fontColor: string }>`
  position: relative;
  width: 100%;
  line-height: 0;
  * {
    line-height: 1;
    font-family: ${({ fontFamily }) => fontFamily};
    color: ${({ fontColor }) => fontColor};
    border-color: ${({ fontColor }) => fontColor};
  }
`

const Name = styled.div`
  position: absolute;
  top: 34%;
  left: 6%;
  width: 25%;
  height: 14%;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    letter-spacing: -2px;
    font-weight: bold;
  }
`

const SexBase = styled.div`
  position: absolute;
  top: 37%;
  left: 30%;
  border-radius: 50%;
  border-style: solid;
  border-width: 3px;
  width: 20px;
  height: 20px;
`

const Sex0 = styled(SexBase)``
const Sex1 = styled(SexBase)`
  left: 35.5%;
`

const VCText = styled.div`
  position: absolute;
  top: 37%;
  left: 23.5%;
  height: 40%;
  width: 20%;
  word-break: break-all;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    font-weight: bold;
    line-height: 1.2;
  }
`

const TimeBase = styled.div`
  position: absolute;
  top: 54.2%;
  left: 4%;
  text-align: center;
  width: 20%;
  font-size: 8px;
  font-weight: bold;
  word-break: break-all;
  line-height: 13px;
`

const TimeWeekDay = styled(TimeBase)``
const TimeWeekEnd = styled(TimeBase)`
  top: 57%;
`

const PlayTime = styled.div`
  position: absolute;
  top: 67%;
  left: 3%;
  text-align: center;
  width: 22%;
  word-break: break-all;
  height: 12%;
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    text-align: center;
    font-weight: bold;
    line-height: 1.2;
  }
`

const FavoriteWeapon = styled.div`
  position: absolute;
  top: 66.5%;
  left: 5%;
  text-align: center;
  width: 13%;
  word-break: break-all;
  height: 12%;
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    text-align: center;
    font-weight: bold;
    line-height: 0.9;
  }
`

const PlayStyle = styled.div`
  position: absolute;
  top: 66.5%;
  left: 25%;
  text-align: center;
  width: 16%;
  word-break: break-all;
  height: 12%;
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    text-align: center;
    font-weight: bold;
    line-height: 0.9;
  }
`

const MaxDamage = styled.span`
  position: absolute;
  top: 59.3%;
  left: 45%;
  text-align: center;
  width: 18%;
  height: 20%;
  font-size: 18px;
  font-weight: bold;
  word-break: break-all;
  line-height: 13px;
`

const KD = styled.span`
  position: absolute;
  top: 59.3%;
  left: 63%;
  text-align: center;
  width: 16%;
  height: 20%;
  font-size: 18px;
  font-weight: bold;
  word-break: break-all;
  line-height: 13px;
`

const LegendBase = styled.div`
  position: absolute;
  top: 10%;
  left: 49%;
  border-style: solid;
  border-width: 2.5px;
  border-radius: 50%;
  width: 40px;
  height: 40px;
`

const Legend0 = styled(LegendBase)``
const Legend1 = styled(LegendBase)`
  left: 60%;
`
const Legend2 = styled(LegendBase)`
  left: 71.5%;
`
const Legend3 = styled(LegendBase)`
  left: 82.5%;
`
const Legend4 = styled(LegendBase)`
  top: 24%;
  left: 49%;
`

const Legend5 = styled(LegendBase)`
  top: 24%;
  left: 60%;
`
const Legend6 = styled(LegendBase)`
  top: 24%;
  left: 71.5%;
`
const Legend7 = styled(LegendBase)`
  top: 24%;
  left: 82.5%;
`
const Legend8 = styled(LegendBase)`
  top: 38%;
  left: 49%;
`
const Legend9 = styled(LegendBase)`
  top: 38%;
  left: 60%;
`

const Legend10 = styled(LegendBase)`
  top: 38%;
  left: 71.5%;
`

const Legend11 = styled(LegendBase)`
  top: 38%;
  left: 82.5%;
`

const Legend12 = styled(LegendBase)`
  top: 51.5%;
  left: 49%;
`

const Legend13 = styled(LegendBase)`
  top: 51.5%;
  left: 60%;
`

const Legend14 = styled(LegendBase)`
  top: 51.5%;
  left: 71.5%;
`

const Legend15 = styled(LegendBase)`
  top: 51.5%;
  left: 82.5%;
`

const Legend16 = styled(LegendBase)`
  top: 65%;
  left: 49%;
`

const Legend17 = styled(LegendBase)`
  top: 65%;
  left: 60%;
`

const Legend18 = styled(LegendBase)`
  top: 78.5%;
  left: 49%;
`

const Legend19 = styled(LegendBase)`
  top: 78.5%;
  left: 60%;
`

const Legend20 = styled(LegendBase)`
  top: 65%;
  left: 77%;
  width: 60px;
  height: 60px;
`

const Hitokoto = styled.div`
  position: absolute;
  top: 83.5%;
  left: 5%;
  height: 10%;
  width: 35%;
  word-break: break-all;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    font-weight: bold;
    line-height: 1.4;
  }
`
